import React from 'react'

import { LoadingSpinner as Loading } from '@gousto-internal/citrus-react'
import type { Location } from 'history'
import { useDispatch, useSelector } from 'react-redux'
import { browserHistory } from 'react-router'

import {
  CONTROL_VIEW_CTA_INTENTS,
  UnsubscribedDashboard,
  MyGoustoPage,
} from '@features/my-gousto-page'
import { MyHomePage } from '@features/my-home-page'

import { actionTypes } from 'actions/actionTypes'
import loginActions from 'actions/login'
import actions from 'actions/user'
import config from 'config/routes'
import { getUserSubscriptionState } from 'selectors/user'
import { addTargetToRedirect } from 'utils/routes'
import { documentLocation } from 'utils/window'

import { ReferAFriendContainer } from './ReferAFriendContainer'
import { useUnsubscribedDashboard } from './UnsubscribedDashboard/useUnsubscribedDashboard'
import { useUnsubscribedDashboardOrderActions } from './UnsubscribedDashboard/useUnsubscribedDashboardOrderActions'
import { GoustoAnytimeModal } from './experiments/GoustoAnytime/GoustoAnytimeModal'
import {
  trackOrderOneOffBoxCtaClick,
  trackResubscribeCtaClick,
} from './experiments/GoustoAnytime/controlViewCtaTracking'
import { useGoustoAnytimeEmptyStateBannerFlag } from './experiments/GoustoAnytime/useGoustoAnytimeEmptyStateBannerFlag'
import { useGoustoAnytimeExperiment } from './experiments/GoustoAnytime/useGoustoAnytimeExperiment'
import { useMyGoustoIntentHandler } from './useMyGoustoIntentHandler'

type MyGoustoPageProps = {
  dispatchCancelOrder: (orderId: string) => void
  dispatchRefreshOrdersState: () => void
  // Only passed for unsubscribed customers; active subscribers never receive it, so the CTAs stay hidden.
  onCtaActionClick?: (intent: string) => void
}

type ActiveSubscriptionMyGoustoProps = {
  dispatchExpiredSessionLogout: () => void
}

const MyGoustoControlView = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  onCtaActionClick,
}: MyGoustoPageProps) => (
  <>
    {/*
      Keep this selector at the route level so app and Cypress tests can
      target the control branch without changing @features/my-gousto-page.
      The feature-modules CI job runs `yarn workspaces foreach` over changed
      `@features/*` workspaces since the branch point, so touching that
      workspace would make its isolated unit suite run as well. That is
      undesirable here because this route-only change would then inherit
      unrelated feature-workspace test/dependency failures.
    */}
    <div data-testid="my-gousto-page">
      <MyGoustoPage
        dispatchCancelOrder={dispatchCancelOrder}
        dispatchRefreshOrdersState={dispatchRefreshOrdersState}
        onCtaActionClick={onCtaActionClick}
      />
    </div>
    <ReferAFriendContainer />
  </>
)

const MyGoustoLoadingView = () => (
  <div
    aria-label="Loading My Gousto"
    data-testid="my-gousto-loading"
    role="status"
    style={{
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'center',
      minHeight: '350px',
    }}
  >
    <Loading />
  </div>
)

const ActiveSubscriptionMyGousto = ({
  dispatchExpiredSessionLogout,
}: ActiveSubscriptionMyGoustoProps) => (
  <div data-testid="my-home-page">
    <MyHomePage onSessionExpired={dispatchExpiredSessionLogout} />
  </div>
)

const UnsubscribedMyGousto = ({
  dispatchCancelOrder,
  dispatchRefreshOrdersState,
  onCtaActionClick,
}: MyGoustoPageProps) => {
  const { isLoading, sections, isEmptyState } = useUnsubscribedDashboard()
  const isNewHeroBannerEnabled = useGoustoAnytimeEmptyStateBannerFlag()
  const { onAction } = useMyGoustoIntentHandler(isEmptyState)
  const orderActions = useUnsubscribedDashboardOrderActions()

  if (isLoading) {
    return <MyGoustoLoadingView />
  }

  if (sections) {
    return (
      <div data-testid="unsubscribed-dashboard-view">
        <UnsubscribedDashboard
          sections={sections}
          onAction={onAction}
          orderActions={orderActions}
          isNewHeroBannerEnabled={isNewHeroBannerEnabled}
        />
      </div>
    )
  }

  return (
    <MyGoustoControlView
      dispatchCancelOrder={dispatchCancelOrder}
      dispatchRefreshOrdersState={dispatchRefreshOrdersState}
      onCtaActionClick={onCtaActionClick}
    />
  )
}

export const MyGousto = () => {
  const { showModal, data, dismiss } = useGoustoAnytimeExperiment()
  const dispatch = useDispatch()
  const userSubscriptionState = useSelector(getUserSubscriptionState)
  const isUserSubscriptionLoading = userSubscriptionState === undefined
  const dispatchCancelOrder = (orderId: string) => {
    dispatch({
      type: actionTypes.ORDER_CANCEL,
      orderId,
      restorable: false,
    })
  }
  const dispatchRefreshOrdersState = () => {
    dispatch(actions.userLoadOrdersForMyGousto(true))
  }
  const dispatchExpiredSessionLogout = React.useCallback(() => {
    const redirectPath = addTargetToRedirect({
      target: true,
      location: documentLocation() as Location,
      redirectUrl: config.client.home,
    })

    dispatch(loginActions.logoutUser({ redirectPath }))
  }, [dispatch])

  const handleCtaAction = (intent: string) => {
    if (intent === CONTROL_VIEW_CTA_INTENTS.resubscribe) {
      trackResubscribeCtaClick()
      browserHistory.push(config.client.resubscribe)

      return
    }

    if (intent === CONTROL_VIEW_CTA_INTENTS.orderOneOffBox) {
      trackOrderOneOffBoxCtaClick()
      // Control customers add their details on /delivery-details before reaching the menu.
      // startOneOffOrder() is the TRX-experiment bypass (straight to /menu) and must stay
      // scoped to the Gousto Anytime variant surfaces, not the control view.
      browserHistory.push(config.client.deliveryDetails)
    }
  }

  return (
    <div>
      {showModal && data && <GoustoAnytimeModal data={data} onDismiss={dismiss} />}
      {isUserSubscriptionLoading ? (
        <MyGoustoLoadingView />
      ) : userSubscriptionState !== 'active' ? (
        <UnsubscribedMyGousto
          dispatchCancelOrder={dispatchCancelOrder}
          dispatchRefreshOrdersState={dispatchRefreshOrdersState}
          onCtaActionClick={handleCtaAction}
        />
      ) : (
        <ActiveSubscriptionMyGousto dispatchExpiredSessionLogout={dispatchExpiredSessionLogout} />
      )}
    </div>
  )
}
